/**
 * Shared web chrome for native `<input type="date|time">`.
 * Keeps OS/browser pickers; only the field surface is Vuelto-styled.
 *
 * Control tokens: inputBackground + controlBorder · radius 12 · hit ≥40.
 */
import type { CSSProperties } from 'react';
import { colors, spacing, typography } from '../theme/public-api';

export const NATIVE_TEMPORAL_RADIUS = 12;
export const NATIVE_TEMPORAL_MIN_HEIGHT = 40;
export const NATIVE_TEMPORAL_COMFORTABLE_HEIGHT = 48;

export type WebNativeTemporalSize = 'default' | 'comfortable' | 'compact';

type StyleOpts = {
  size?: WebNativeTemporalSize;
  /** Time fields often sit narrower in a row. */
  flex?: number | string;
  minWidth?: number | string;
  width?: number | string;
  fontFamily?: string;
  fontWeight?: CSSProperties['fontWeight'];
  fontSize?: number;
  textAlign?: CSSProperties['textAlign'];
};

function heightFor(size: WebNativeTemporalSize): number {
  if (size === 'comfortable') return NATIVE_TEMPORAL_COMFORTABLE_HEIGHT;
  if (size === 'compact') return NATIVE_TEMPORAL_MIN_HEIGHT;
  return NATIVE_TEMPORAL_MIN_HEIGHT;
}

/**
 * CSS for createElement('input', { type: 'date' | 'time', style }).
 * Use on web only.
 */
export function webNativeTemporalInputStyle(opts: StyleOpts = {}): CSSProperties {
  const size = opts.size ?? 'default';
  const h = heightFor(size);
  const padX = size === 'comfortable' ? spacing.md : size === 'compact' ? spacing.sm : spacing.md;
  return {
    ...(opts.flex !== undefined ? { flex: opts.flex } : { width: '100%' }),
    minWidth: opts.minWidth ?? 0,
    ...(opts.width !== undefined ? { width: opts.width } : null),
    minHeight: h,
    height: h,
    borderRadius: NATIVE_TEMPORAL_RADIUS,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.controlBorder,
    backgroundColor: colors.inputBackground,
    color: colors.textPrimary,
    paddingLeft: padX,
    paddingRight: padX,
    fontSize: opts.fontSize ?? typography.body.fontSize,
    fontWeight: opts.fontWeight ?? '500',
    fontFamily: opts.fontFamily,
    lineHeight: `${h - 2}px`,
    textAlign: opts.textAlign,
    outlineStyle: 'none',
    boxSizing: 'border-box',
    colorScheme: 'light',
    fontVariantNumeric: 'tabular-nums',
  };
}

/** Android/iOS trigger chrome matching web native temporal fields. */
export const nativeTemporalTriggerStyle = {
  flexDirection: 'row' as const,
  alignItems: 'center' as const,
  justifyContent: 'space-between' as const,
  gap: spacing.sm,
  borderWidth: 1,
  borderColor: colors.controlBorder,
  borderRadius: NATIVE_TEMPORAL_RADIUS,
  backgroundColor: colors.inputBackground,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.sm,
  minHeight: NATIVE_TEMPORAL_MIN_HEIGHT,
};
